import AppMenuBar from '@/components/app/layout/app-menu-bar'
import ResizeLayoutDialog from '@/components/app/layout/resize-layout-dialog'
import VersionInfo from '@/components/app/common/version-info'
import CenterCanvas from '@/components/app/editor/center-canvas'

import { LeftPanel } from '@/components/app/panels/left-panel'
import RightPanel from '@/components/app/panels/right-panel'

import NavBar from '@/components/app/layout/navbar'

import { useMemo } from 'react'
import { WebSocketProvider } from '@/providers/websocket'
import { ColorPaletteProvider } from '@/contexts/color-palette-context'
import { useColorsQuery } from '@/api/hooks/use-colors-query'
import { ConnectionStatusBanner } from '@/components/app/common/connection-status-banner'

import { FontProvider } from '@/providers/font'

import { ActiveObjectProvider } from '@/components/app/editor/active-object-context'

import { useStableCallbacks } from '@/hooks/use-stable-callbacks'
import type { Guide } from '@/lib/guides/types/guide'

import { EDITOR_ZOOM_MAX, EDITOR_ZOOM_MIN } from '@/lib/zoom/editor-zoom'

import { WebSocketAuthHandler } from './components/websocket-auth-handler'
import { MultiplayerSyncHandler } from './components/multiplayer-sync-handler'
import { DictionarySyncHandler } from './components/dictionary-sync-handler'
import { MissingFontsChecker } from './components/missing-fonts-checker'
import { BlockedFontsChecker } from './components/blocked-fonts-checker'
import { ClikArtCatalogWarmup } from './components/clikart-catalog-warmup'
import { ClikArtPlacedIdsSync } from './components/clikart-placed-ids-sync'
import { TextChainFontsBridge } from './components/text-chain-fonts-bridge'
import { EditorPlacementProviders } from './components/editor-placement-providers'
import { EditorWarningDialogs } from './components/editor-warning-dialogs'
import { EditorProviders } from './components/editor-providers'
import { EMPTY_GUIDES } from './constants/empty-guides'
import { useEditorContextValues } from './hooks/use-editor-context-values'

import { useHomeFoundation } from './hooks/groups/use-home-foundation'
import { useHomeZoomAndSpread } from './hooks/groups/use-home-zoom-and-spread'
import { useHomeDocumentLayout } from './hooks/groups/use-home-document-layout'
import { useHomeEditorSettings } from './hooks/groups/use-home-editor-settings'
import { useHomeToolsAndSelection } from './hooks/groups/use-home-tools-and-selection'
import { useHomePersistence } from './hooks/groups/use-home-persistence'
import { useHomeHistory } from './hooks/groups/use-home-history'
import { useHomeZoomCommands } from './hooks/groups/use-home-zoom-commands'
import { useHomeTextChains } from './hooks/groups/use-home-text-chains'
import { useHomeShapeEditing } from './hooks/groups/use-home-shape-editing'
import { useHomeShapePersistence } from './hooks/groups/use-home-shape-persistence'
import { useHomeChainEditing } from './hooks/groups/use-home-chain-editing'
import { useHomeCheckpointView } from './hooks/groups/use-home-checkpoint-view'
import { useHomeMenuCommands } from './hooks/groups/use-home-menu-commands'

/** Page count placeholder (rebase branch); export avoids unused-local noise until wired into UI. */
export const TOTAL_PAGES = 64

/**
 * The editor screen, composed from the home group hooks. Call order is load-bearing: each group
 * reads the groups before it, and their effects run in this order, so a group must not move above
 * one it depends on (see the groups' docs in hooks/groups/).
 */
function HomeInner() {
  const foundation = useHomeFoundation()
  const {
    token,
    username,
    isCornerResizeActive,
    textChains,
    unifiedLayerIds,
    textPanelRevision,
    shapeManagerRef,
    centerCanvasRef,
    leftPanelRef,
    fontLookupRef,
    fontFamiliesRef,
    selectedShapeTextParagraphsForPanel,
    isSelectedShapeTextFrameForPanel,
    zoomDropdownAlignRef,
    rightPanelStackSlotRef,
  } = foundation
  const zoomAndSpread = useHomeZoomAndSpread()
  const {
    data,
    isSpreadCheckpointLoaded,
    zoom,
    setZoom,
    setEffectiveCanvasZoom,
    zoomToolbarKeyFlash,
    spreadIndex,
    zoomAriaAnnouncement,
    photoPlacementAnnouncement,
    setPhotoPlacementAnnouncement,
    photoPlacementAriaKey,
    setPhotoPlacementAriaKey,
    photoSizeWarningState,
    textToImageWarningState,
    clikArtSizeWarningState,
    clikArtTextToImageWarningState,
    zoomAriaLiveKey,
    handleFitToSpreadAvailabilityChange,
    effectiveEditorZoom,
    canZoomIn,
    canZoomOut,
    orderedSpreads,
    spreadId,
    spreadIdRef,
    hasSpreadsList,
    navigationMaxIndex,
    spreadPageIndicator,
    spreadNavigationLocked,
  } = zoomAndSpread
  const layout = useHomeDocumentLayout({ ...zoomAndSpread })
  const {
    snapToGrid,
    guidesPerSpread,
    setGuidesPerSpread,
    isResizeLayoutDialogOpen,
    setIsResizeLayoutDialogOpen,
    totalPages,
    pageViewMode,
    fitToSpreadDisabled,
    spreadPlacementBounds,
  } = layout

  const editorSettings = useHomeEditorSettings({ ...zoomAndSpread, ...layout })
  const { handleGuidesDialogOpenChange } = editorSettings
  const toolsAndSelection = useHomeToolsAndSelection(foundation)
  const { panAnnouncement, canZoomToSelection, deactivatePanMode } = toolsAndSelection
  const persistence = useHomePersistence({
    ...foundation,
    ...zoomAndSpread,
    ...layout,
    ...toolsAndSelection,
  })
  const {
    markEdited,
    restoreSpreadBackgroundsRef,
    flushSpreadBackgroundWritesRef,
    recordSpreadBackgroundsChange,
  } = persistence

  const history = useHomeHistory({ ...foundation, ...zoomAndSpread, ...layout, ...persistence })
  const zoomCommands = useHomeZoomCommands({
    ...foundation,
    ...zoomAndSpread,
    ...layout,
    ...toolsAndSelection,
    ...history,
  })

  const textChainEngine = useHomeTextChains({
    ...foundation,
    ...zoomAndSpread,
    ...layout,
    ...persistence,
    ...history,
  })
  const { projectChainStoryLive, projectAllChainsLive } = textChainEngine
  const shapeEditing = useHomeShapeEditing({
    ...foundation,
    ...zoomAndSpread,
    ...layout,
    ...toolsAndSelection,
    ...persistence,
    ...history,
    ...textChainEngine,
  })
  const {
    handleShapeCreated,
    handleDeleteGestureScope,
    handlePhotoDropBlocked,
    handleFillShapeWithPhoto,
    handleTextToImageWarningContinue,
    handleTextToImageWarningCancel,
    handlePhotoSizeWarningContinue,
    handlePhotoSizeWarningCancel,
    handleClikArtDropBlocked,
    handleFillShapeWithClikArt,
    handleFillShapeWithColor,
    handlePlaceTemplate,
    handleClikArtTextToImageWarningContinue,
    handleClikArtTextToImageWarningCancel,
    handleClikArtSizeWarningContinue,
    handleClikArtSizeWarningCancel,
    handleBatchShapesCommittedRef,
  } = shapeEditing

  const shapePersistence = useHomeShapePersistence({
    ...foundation,
    ...zoomAndSpread,
    ...layout,
    ...toolsAndSelection,
    ...persistence,
    ...history,
    ...textChainEngine,
    handleBatchShapesCommittedRef,
  })
  const {
    handleShapeCommitted,
    handleBatchShapesCommitted,
    handleLayerReorder,
    handleShapeDeleted,
  } = shapePersistence

  const chainEditing = useHomeChainEditing({
    ...foundation,
    ...zoomAndSpread,
    ...layout,
    ...toolsAndSelection,
    ...persistence,
    ...textChainEngine,
  })
  const {
    handleChainBackspaceAtStart,
    handleShapeChainOverflowCaret,
    handleChainArrowLeftAtStart,
    handleChainArrowRightAtEnd,
    handleRemoteTextBoxDeleted,
    handleTextFramesLinked,
    handleTextFramesUnlinked,
    handleCreateLinkedTextFrame,
  } = chainEditing
  const checkpointView = useHomeCheckpointView({
    ...foundation,
    ...zoomAndSpread,
    ...layout,
    ...persistence,
    ...shapePersistence,
  })
  const {
    checkpointShapes,
    checkpointDiagnostics,
    pageTitles,
    elementZIndexMap,
    handleUnifiedLayerArrange,
  } = checkpointView

  const menuCommands = useHomeMenuCommands({
    ...foundation,
    ...zoomAndSpread,
    ...layout,
    ...toolsAndSelection,
    ...persistence,
    ...history,
    ...shapeEditing,
  })
  const {
    handleRightPanelOpened,
    handleRightPanelEscapeFallbackToSelectTool,
    handleRightPanelEscapeClearPhotoSelection,
  } = menuCommands

  const editorContextValues = useEditorContextValues({
    ...foundation,
    ...zoomAndSpread,
    ...layout,
    ...editorSettings,
    ...toolsAndSelection,
    ...persistence,
    ...history,
    ...zoomCommands,
    ...shapeEditing,
    ...chainEditing,
    ...menuCommands,
  })
  const { commands } = editorContextValues

  const { data: colors } = useColorsQuery()
  const palette = useMemo(() => new Map(colors?.map((c) => [c.colorId, c]) ?? []), [colors])

  // Callbacks handed to the memoized layout roots (CenterCanvas, LeftPanel): stable identities that
  // always call the latest handler, so a home re-render alone never re-renders those trees.
  const editorCallbacks = useStableCallbacks({
    onUserEdit: markEdited,
    onShapeCreated: handleShapeCreated,
    onShapeCommitted: handleShapeCommitted,
    onBatchShapesCommitted: handleBatchShapesCommitted,
    onShapeDeleted: handleShapeDeleted,
    onDeleteGestureScope: handleDeleteGestureScope,
    onLayerReorder: handleLayerReorder,
    onUnifiedLayerArrange: handleUnifiedLayerArrange,
    onTextFramesLinked: handleTextFramesLinked,
    onTextFramesUnlinked: handleTextFramesUnlinked,
    onCreateLinkedTextFrame: handleCreateLinkedTextFrame,
    onChainBackspaceAtStart: handleChainBackspaceAtStart,
    onChainArrowLeftAtStart: handleChainArrowLeftAtStart,
    onChainArrowRightAtEnd: handleChainArrowRightAtEnd,
    onShapeChainOverflowCaret: handleShapeChainOverflowCaret,
    onShapeTextLiveEdit: projectChainStoryLive,
    onLiveWrapChainSync: projectAllChainsLive,
    onZoomChange: (z: number) => setZoom(() => +z.toFixed(3)),
    onEffectiveZoomLevelChange: setEffectiveCanvasZoom,
    onFitToSpreadAvailabilityChange: handleFitToSpreadAvailabilityChange,
    onGuidesChange: (nextGuides: Guide[]) =>
      setGuidesPerSpread((prev) => ({ ...prev, [spreadId]: nextGuides })),
    onRightPanelOpened: handleRightPanelOpened,
    onRightPanelEscapeFallbackToSelectTool: handleRightPanelEscapeFallbackToSelectTool,
    onRightPanelEscapeClearPhotoSelection: handleRightPanelEscapeClearPhotoSelection,
    // Next/Previous spread closing Backgrounds lands on the Select tool, like the Escape close.
    onRightPanelClosedByRequest: handleRightPanelEscapeFallbackToSelectTool,
    onPlacementAnnounce: (message: string) => {
      setPhotoPlacementAriaKey((k) => k + 1)
      setPhotoPlacementAnnouncement(message)
    },
  })

  return (
    <WebSocketProvider token={token} autoConnect={true}>
      <WebSocketAuthHandler />
      <MultiplayerSyncHandler
        spreadId={spreadId}
        shapeManagerRef={shapeManagerRef}
        enabled={Boolean(spreadId)}
        onRemoteTextBoxDeleted={handleRemoteTextBoxDeleted}
      />
      <DictionarySyncHandler />
      <ConnectionStatusBanner />
      <ResizeLayoutDialog
        open={isResizeLayoutDialogOpen}
        onCancel={() => setIsResizeLayoutDialogOpen(false)}
        onResize={() => {
          setIsResizeLayoutDialogOpen(false)
          handleGuidesDialogOpenChange(true)
        }}
      />
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {panAnnouncement}
      </div>
      <EditorProviders values={editorContextValues}>
        <ColorPaletteProvider value={palette}>
          <div className="h-screen w-full flex flex-col">
            {/* Top Menubar */}
            <AppMenuBar
              username={username}
              onLogout={commands.logout}
              onClose={commands.closeEditor}
              onNavigate={commands.navigateSpread}
              spreadIndex={spreadIndex}
              onSpreadIndexChange={commands.changeSpreadIndex}
              totalPages={totalPages}
              maxSpreadIndex={hasSpreadsList ? navigationMaxIndex : undefined}
              spreadPageIndicator={hasSpreadsList ? spreadPageIndicator : undefined}
              spreadNavigationLocked={spreadNavigationLocked}
              pageViewMode={pageViewMode}
              leftText={pageTitles.left}
              rightText={pageTitles.right}
            />

            {/* Main content with panels moved here */}
            <EditorPlacementProviders
              shapeManagerRef={shapeManagerRef}
              centerCanvasRef={centerCanvasRef}
              spreadIdRef={spreadIdRef}
              spreadPlacementBounds={spreadPlacementBounds}
              snapToGrid={snapToGrid}
              onScreenReaderAnnounce={editorCallbacks.onPlacementAnnounce}
              onFillShapeWithColor={handleFillShapeWithColor}
              onFillShapeWithClikArt={handleFillShapeWithClikArt}
              onClikArtDropBlocked={handleClikArtDropBlocked}
              onFillShapeWithPhoto={handleFillShapeWithPhoto}
              onPhotoDropBlocked={handlePhotoDropBlocked}
              onPlaceTemplate={handlePlaceTemplate}
              spreadId={spreadId}
              checkpointMetadata={data?.metadata}
              isCheckpointLoaded={isSpreadCheckpointLoaded}
              restoreSpreadBackgroundsRef={restoreSpreadBackgroundsRef}
              flushSpreadBackgroundWritesRef={flushSpreadBackgroundWritesRef}
              onSpreadBackgroundsChanged={recordSpreadBackgroundsChange}
            >
              <div className="flex-1 flex overflow-hidden">
                <FontProvider spreadId={spreadId || undefined}>
                  <MissingFontsChecker
                    shapeManagerRef={shapeManagerRef}
                    checkApplied={textPanelRevision}
                    onShapeCommitted={handleShapeCommitted}
                  />
                  <BlockedFontsChecker
                    shapeManagerRef={shapeManagerRef}
                    onShapeCommitted={handleShapeCommitted}
                  />
                  <ClikArtCatalogWarmup isCheckpointLoaded={isSpreadCheckpointLoaded} />
                  <ClikArtPlacedIdsSync isCheckpointLoaded={isSpreadCheckpointLoaded} />
                  <TextChainFontsBridge
                    fontLookupRef={fontLookupRef}
                    familiesRef={fontFamiliesRef}
                  />
                  <LeftPanel
                    ref={leftPanelRef}
                    textPanelRevision={textPanelRevision}
                    shapeManagerRef={shapeManagerRef}
                    isCornerResizeActive={isCornerResizeActive}
                    onShapeCommitted={editorCallbacks.onShapeCommitted}
                    onBatchShapesCommitted={editorCallbacks.onBatchShapesCommitted}
                    onLayerReorder={editorCallbacks.onLayerReorder}
                    onUnifiedLayerArrange={editorCallbacks.onUnifiedLayerArrange}
                    unifiedLayerIds={unifiedLayerIds}
                    textChains={textChains}
                    onTextFramesLinked={editorCallbacks.onTextFramesLinked}
                    selectedShapeTextParagraphs={selectedShapeTextParagraphsForPanel}
                    isSelectedShapeTextFrame={isSelectedShapeTextFrameForPanel}
                  />
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="sr-only" role="status" aria-live="assertive" aria-atomic="true">
                      {zoomAriaAnnouncement ? (
                        <span key={zoomAriaLiveKey}>{zoomAriaAnnouncement}</span>
                      ) : null}
                    </div>
                    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                      {photoPlacementAnnouncement ? (
                        <span key={photoPlacementAriaKey}>{photoPlacementAnnouncement}</span>
                      ) : null}
                    </div>
                    <NavBar
                      className="flex-shrink-0 h-14 sm:h-16"
                      zoomDropdownAlignRef={zoomDropdownAlignRef}
                      zoomToolbarKeyFlash={zoomToolbarKeyFlash}
                      zoomInDisabled={!canZoomIn}
                      zoomOutDisabled={!canZoomOut}
                      zoomPercent={effectiveEditorZoom * 100}
                      fitToSpreadDisabled={fitToSpreadDisabled}
                      zoomToSelectionDisabled={!canZoomToSelection}
                      zoomMinPercent={EDITOR_ZOOM_MIN * 100}
                      zoomMaxPercent={EDITOR_ZOOM_MAX * 100}
                    />
                    {/* The spread area. From `md` up the wrapper is `display: contents`, so the
                        canvas lays out exactly as a direct child of this column. Below it, the
                        spread and the right panel's stack slot share the column: both regions get
                        rounded corners and a border (WMPCM-11050 AC-1). */}
                    <div
                      data-spread-area="true"
                      className="md:contents max-md:mx-2 max-md:my-2 max-md:min-h-0 max-md:flex-1 max-md:overflow-hidden max-md:rounded-lg max-md:border max-md:border-border"
                    >
                      <CenterCanvas
                        ref={centerCanvasRef}
                        zoom={zoom}
                        guides={guidesPerSpread[spreadId] ?? EMPTY_GUIDES}
                        checkpointShapes={checkpointShapes}
                        checkpointDiagnostics={checkpointDiagnostics}
                        elementZIndexMap={elementZIndexMap}
                        unifiedLayerIds={unifiedLayerIds}
                        linkedTextChains={textChains}
                        shapeManagerRef={shapeManagerRef}
                        onUserEdit={editorCallbacks.onUserEdit}
                        onShapeCreated={editorCallbacks.onShapeCreated}
                        onShapeCommitted={editorCallbacks.onShapeCommitted}
                        onBatchShapesCommitted={editorCallbacks.onBatchShapesCommitted}
                        onShapeDeleted={editorCallbacks.onShapeDeleted}
                        onDeleteGestureScope={editorCallbacks.onDeleteGestureScope}
                        onLayerReorder={editorCallbacks.onLayerReorder}
                        onUnifiedLayerArrange={editorCallbacks.onUnifiedLayerArrange}
                        onTextFramesLinked={editorCallbacks.onTextFramesLinked}
                        onTextFramesUnlinked={editorCallbacks.onTextFramesUnlinked}
                        onCreateLinkedTextFrame={editorCallbacks.onCreateLinkedTextFrame}
                        onChainBackspaceAtStart={editorCallbacks.onChainBackspaceAtStart}
                        onChainArrowLeftAtStart={editorCallbacks.onChainArrowLeftAtStart}
                        onChainArrowRightAtEnd={editorCallbacks.onChainArrowRightAtEnd}
                        onShapeChainOverflowCaret={editorCallbacks.onShapeChainOverflowCaret}
                        onShapeTextLiveEdit={editorCallbacks.onShapeTextLiveEdit}
                        onLiveWrapChainSync={editorCallbacks.onLiveWrapChainSync}
                        onZoomChange={editorCallbacks.onZoomChange}
                        onEffectiveZoomLevelChange={editorCallbacks.onEffectiveZoomLevelChange}
                        onFitToSpreadAvailabilityChange={
                          editorCallbacks.onFitToSpreadAvailabilityChange
                        }
                        onGuidesChange={editorCallbacks.onGuidesChange}
                      />
                    </div>
                    {/* The right panel portals itself here on narrow viewports (`StackedPanel`). */}
                    <div ref={rightPanelStackSlotRef} className="contents" />
                  </div>
                </FontProvider>
                <RightPanel
                  spreadId={spreadId}
                  edgeRef={zoomDropdownAlignRef}
                  stackSlotRef={rightPanelStackSlotRef}
                  onPanelActivate={deactivatePanMode}
                  onPanelOpened={editorCallbacks.onRightPanelOpened}
                  onEscapeFallbackToSelectTool={
                    editorCallbacks.onRightPanelEscapeFallbackToSelectTool
                  }
                  onEscapeClearPhotoSelectionAndCloseLeftPanel={
                    editorCallbacks.onRightPanelEscapeClearPhotoSelection
                  }
                  onClosedByRequest={editorCallbacks.onRightPanelClosedByRequest}
                />
              </div>
            </EditorPlacementProviders>

            <EditorWarningDialogs
              photoSize={{
                open: photoSizeWarningState !== null,
                onCancel: handlePhotoSizeWarningCancel,
                onContinue: handlePhotoSizeWarningContinue,
              }}
              photoTextToImage={{
                open: textToImageWarningState !== null,
                onCancel: handleTextToImageWarningCancel,
                onContinue: handleTextToImageWarningContinue,
              }}
              clikArtSize={{
                open: clikArtSizeWarningState !== null,
                onCancel: handleClikArtSizeWarningCancel,
                onContinue: handleClikArtSizeWarningContinue,
              }}
              clikArtTextToImage={{
                open: clikArtTextToImageWarningState !== null,
                onCancel: handleClikArtTextToImageWarningCancel,
                onContinue: handleClikArtTextToImageWarningContinue,
              }}
            />

            <VersionInfo
              position="bottom-right"
              showBuildInfo={true}
              showEnvironment={true}
              minimal={false}
              spreadId={spreadId}
              currentSpread={orderedSpreads.length ? spreadIndex + 1 : undefined}
              totalSpreads={orderedSpreads.length || undefined}
              pageViewMode={pageViewMode}
            />
          </div>
        </ColorPaletteProvider>
      </EditorProviders>
    </WebSocketProvider>
  )
}

export default function HomeWithProviders() {
  return (
    <ActiveObjectProvider>
      <HomeInner />
    </ActiveObjectProvider>
  )
}
